import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { cooDefaults, cooReport, type CooInputs } from "@/lib/tools/calc";
import { NumberField, ToolPageShell, toolHead } from "@/components/site/tools/ToolPageShell";
import { CooChart } from "@/components/site/tools/ToolCharts";

export const Route = createFileRoute("/tools/fractional-coo-cost-calculator")({
  head: () => toolHead("fractional-coo-cost-calculator"),
  component: CooCostPage,
});

function CooCostPage() {
  const [i, setI] = useState<CooInputs>(cooDefaults);
  const set = (k: keyof CooInputs) => (v: number | null) => setI((p) => ({ ...p, [k]: k === "retainer" ? v : (v ?? 0) }));
  return (
    <ToolPageShell
      slug="fractional-coo-cost-calculator"
      report={cooReport(i)}
      inputs={i}
      chart={<CooChart inputs={i} />}
      form={
        <div className="space-y-6">
          <fieldset>
            <legend className="font-semibold text-midnight">Full time COO</legend>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <NumberField id="coo-salary" label="Base salary" prefix="$" value={i.salary} onChange={set("salary")} min={0} />
              <NumberField id="coo-super" label="Super" suffix="%" value={i.superPct} onChange={set("superPct")} min={0} max={50} />
              <NumberField id="coo-on" label="On costs" suffix="%" value={i.onCostPct} onChange={set("onCostPct")} min={0} max={100} hint="Leave, payroll tax, insurance, equipment." />
              <NumberField id="coo-fee" label="Recruitment fee" suffix="%" value={i.recruitPct} onChange={set("recruitPct")} min={0} max={100} />
              <NumberField id="coo-ramp" label="Months to recruit and get up to speed" value={i.rampMonths} onChange={set("rampMonths")} min={0} max={24} />
            </div>
          </fieldset>
          <fieldset>
            <legend className="font-semibold text-midnight">Fractional COO</legend>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <NumberField
                id="coo-retainer"
                label="Monthly retainer"
                prefix="$"
                value={i.retainer}
                onChange={set("retainer")}
                min={0}
                placeholder="Enter the figure quoted to you"
                hint={<>Use the figure you were quoted. Published fees are on the <Link to="/packages" className="underline underline-offset-2">packages page</Link>.</>}
              />
              <NumberField id="coo-months" label="Months engaged in year one" value={i.months} onChange={set("months")} min={0} max={12} step={1} />
            </div>
          </fieldset>
        </div>
      }
    />
  );
}
